import { Injectable, Optional } from '@angular/core';
import { concat, every, map, Observable, of, switchMap, zip } from 'rxjs';
import { AsyncConditionDecorator } from './conditions/async-condition-decorator';
import { AssertionResults, Condition, Engine, Rule } from './model/rule-engine.model';
import { collapse } from './utils/collapse';
import { isNull } from './utils/is-null';
import { pick } from './utils/pick';

@Injectable()
export class RuleEngineService<T> implements Engine<T> {
  rules: Rule<T>[] = [];
  fact: T = null;

  constructor(@Optional() opts: { rules?: Rule<T>[]; fact?: T } = {}) {
    if (opts.rules) this.rules = opts.rules;
    if (opts.fact) this.fact = opts.fact;
  }

  /**
   * Adds rule to the engine
   * @param rule
   * @returns
   */
  addRule(rule: Rule<T>) {
    this.rules.push(rule);
    return this;
  }

  /**
   * Sets the engine facts to be used when calling evaluate
   * @param fact
   * @returns
   */
  setContext(fact: T) {
    this.fact = fact;
    return this;
  }

  evaluate(alternateFact?: T): Observable<Partial<T>> {
    // set facts to use
    const fact = alternateFact || this.fact;

    // build assertions Observable<AssertionResult[]>
    const ruleAssertions$ = this.rules.map(rule =>
      concat(...rule.when.map(condition => this._assertCondition$(fact, condition))).pipe(
        map(this.toAssertionResults(rule))
      )
    );
    // combine all
    return zip(ruleAssertions$).pipe(
      map(assertionResults => this._applyApplicableRulesAnReturnFactOutput(fact, assertionResults))
    );
  }

  private readonly _assertCondition$ = (fact: T, condition: Condition<T>): Observable<boolean> => {
    const { or: orConditions } = condition;
    const asyncCondition$ = AsyncConditionDecorator(condition);
    return asyncCondition$.assert(fact).pipe(
      switchMap(isConditionMet => {
        // if assertionResult is true OR if assertionResult is false and no "OR" condition is configured
        if (isConditionMet || (!isConditionMet && isNull(orConditions))) {
          return of(isConditionMet);
        }

        // if condition is NOT met and "or" condition is available then return assertion on fallback conditions
        return concat(...orConditions.map(otherCondition => this._assertCondition$(fact, otherCondition))).pipe(
          every(Boolean)
        );
      })
    );
  };

  private readonly toAssertionResults = (rule: Rule<T>): ((x: boolean) => AssertionResults<T>) => {
    return result => ({ rule, result });
  };

  private readonly _applyApplicableRulesAnReturnFactOutput = (fact: T, assertionResults: AssertionResults<T>[]) => {
    const applicableRules = this._getApplicableRules(assertionResults);
    const appliedRulesOutcomes = this._applyRules(fact, applicableRules);
    return collapse(appliedRulesOutcomes);
  };

  private readonly _getApplicableRules = <T>(assertionResults: AssertionResults<T>[]): Rule<T>[] => {
    return assertionResults.filter(a => a.result === true).map(pick('rule'));
  };

  private _applyRules(fact: T, rules: Rule<T>[]): Partial<T>[] {
    return rules.map(rule => collapse(rule.then.map(action => action.apply(fact))));
  }
}
